import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from "@mui/material";
import { LibraryBooks } from "@mui/icons-material";
import { apiV2 } from "../../actions/index";
import { uploadImage, clearMedia } from "../../actions/mediaActions";
import { fetchAllCategories } from "../../actions/categoryActions";
import SortableSeriesList from '../Common/SortableList/SortableSeriesList';
import {
  deleteSeries,
  addEditSeries,
} from "../../actions/seriesActions";
import ImageContainerPreview from '../Common/ImageContainerPreview';
import LoadingBackdrop from "../Common/LoadingBackdrop";
import Swal from "sweetalert2";
import { preventEnterFormSubmission } from '../../utils/formUtils';
import UniversalLinkField from '../Common/UniversalLinkField';

const AddEditSeries = forwardRef(({ isEdit, selectedId }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const all_categories = useSelector(state => state.categories.all_categories);
  const image = useSelector(state => state.media.image);
  const [loading, setLoading] = useState(false);

  const [state, setState] = useState({
    series_title: "",
    series_description: "",
    series_is_active: true,
    series_image: "",
    series_image_preview: "",
    series_image_uploading: false,
    series_text_image_url: "",
    series_text_image_url_preview: "",
    series_text_image_url_uploading: false,
    series_is_dark_mode: false,
    series_is_new: false,
    category: 0,
    sessions: [],
    isSetToEdit: false,
    sessionData: {},
    sortOrder: []
  });

  useEffect(() => {
    dispatch(fetchAllCategories());
    dispatch(clearMedia());

    if (isEdit && selectedId) {
      setLoading(true);
      apiV2.get(`/series/${selectedId}`)
        .then(response => {
          const data = response.data;
          const sessionData = {};
          const sortOrder = [];

          if (data.sessions && Array.isArray(data.sessions)) {
            data.sessions.forEach(s => {
              sessionData[s.session_id] = {
                series_day: s.series_day || 0,
                session_is_dark_mode: !!s.session_is_dark_mode
              };
              sortOrder.push(s.session_id.toString());
            });
          }

          setState(prev => ({
            ...prev,
            series_id: data.series_id,
            series_title: data.series_title || "",
            series_description: data.series_description || "",
            series_is_active: !!data.series_is_active,
            series_image: data.series_image || "",
            series_is_dark_mode: !!data.series_is_dark_mode,
            series_is_new: !!data.series_is_new,
            series_text_image_url: data.series_text_image_url || "",
            universal_link: data.universal_link || "",
            category: data.category_id || 0,
            isSetToEdit: true,
            sessionData,
            sortOrder,
            // Keep session objects for name lookup
            _sessions: data.sessions || [],
          }));
        })
        .catch(error => {
          console.error('Failed to fetch series:', error);
        })
        .finally(() => setLoading(false));
    }
  }, [dispatch, isEdit, selectedId]);

  useEffect(() => {
    if (image) {
      if (state.series_image_uploading) {
        setState(prev => ({
          ...prev,
          series_image: image,
          series_image_uploading: false,
        }));
      }

      if (state.series_text_image_url_uploading) {
        setState(prev => ({
          ...prev,
          series_text_image_url: image,
          series_text_image_url_uploading: false,
        }));
      }
      dispatch(clearMedia());
    }
  }, [image, state.series_image_uploading, state.series_text_image_url_uploading, dispatch]);

  const handleSessionDataChange = useCallback((id, data) => {
    setState(prev => ({
      ...prev,
      sessionData: {
        ...prev.sessionData,
        [id]: {
          series_day: parseInt(data.series_day) || 0,
          session_is_dark_mode: !!data.session_is_dark_mode
        }
      }
    }));
  }, []);

  const handleFieldChange = useCallback((e) => {
    const name = e.target.name;
    const value = e.target.value;
    setState(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (state.series_image_preview) {
      window.URL.revokeObjectURL(state.series_image_preview);
    }
    setState(prev => ({
      ...prev,
      series_image_preview: window.URL.createObjectURL(file),
      series_image_uploading: true,
    }));
    dispatch(uploadImage(file));
  }, [state.series_image_preview, dispatch]);

  const handleDeepLinkImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (state.series_text_image_url_preview) {
      window.URL.revokeObjectURL(state.series_text_image_url_preview);
    }
    setState(prev => ({
      ...prev,
      series_text_image_url_preview: window.URL.createObjectURL(file),
      series_text_image_url_uploading: true,
    }));
    dispatch(uploadImage(file));
  }, [state.series_text_image_url_preview, dispatch]);

  const submit = useCallback(() => {
    Swal.fire({
      title: "Saving...",
      animation: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });

    const { category, sessions: session_ids_json, ...series_json } = state;

    const formattedSessions = state.sortOrder.map(sessionId => ({
      [sessionId]: {
        series_day: state.sessionData[sessionId]?.series_day || 0,
        session_is_dark_mode: state.sessionData[sessionId]?.session_is_dark_mode || 0
      }
    }));

    const options = {
      category_id: category,
      series_json: JSON.stringify(series_json),
      session_ids_json: JSON.stringify(formattedSessions),
    };

    dispatch(addEditSeries(options, () => navigate('/series')));
  }, [state, dispatch]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    submit();
  }, [submit]);

  const handleDelete = useCallback((e) => {
    e.preventDefault();
    Swal.fire({
      icon: "warning",
      text: "Are you sure you want to delete this series?",
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Delete Series",
    }).then((result) => {
      if (result.value) {
        dispatch(deleteSeries(state.series_id, () => navigate('/series')));
      }
    });
  }, [state.series_id, dispatch, navigate]);

  useImperativeHandle(ref, () => ({
    submit
  }), [submit]);

  if (!all_categories || loading) {
    return <LoadingBackdrop open={true} />;
  }

  const sortableItems = state.sortOrder.map(sessionId => {
    const session = (state._sessions || []).find(s => s.session_id.toString() === sessionId);
    const sessionData = state.sessionData[sessionId] || {};

    return {
      id: sessionId,
      label: session ? session.session_name : 'Unknown Session',
      seriesDay: sessionData.series_day || 0,
      isDarkMode: !!sessionData.session_is_dark_mode
    };
  });

  return (
    <Box component="form" onSubmit={handleSubmit} onKeyDown={preventEnterFormSubmission} autoComplete="off">
      <Box display="grid" gap={4}>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }} gap={3}>
          <Box>
            <TextField
              fullWidth
              label="Series Name"
              name="series_title"
              value={state.series_title}
              onChange={handleFieldChange}
              required
            />
          </Box>
          <Box>
            <FormControl fullWidth>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                name="category"
                value={state.category}
                label="Category"
                onChange={handleFieldChange}
              >
                <MenuItem value={0}>None</MenuItem>
                {all_categories && all_categories.map((category) => (
                  <MenuItem key={category.category_id} value={category.category_id}>
                    {category.category_title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  name="series_is_active"
                  checked={!!state.series_is_active}
                  onChange={(e) => setState(prev => ({ ...prev, series_is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  name="series_is_new"
                  checked={!!state.series_is_new}
                  onChange={(e) => setState(prev => ({ ...prev, series_is_new: e.target.checked }))}
                />
              }
              label="Is New"
            />
            <FormControlLabel
              control={
                <Switch
                  name="series_is_dark_mode"
                  checked={!!state.series_is_dark_mode}
                  onChange={(e) => setState(prev => ({ ...prev, series_is_dark_mode: e.target.checked }))}
                />
              }
              label="Dark Mode"
            />
          </Box>
        </Box>

        <Box>
          <TextField
            fullWidth
            label="Description"
            name="series_description"
            value={state.series_description}
            onChange={handleFieldChange}
            multiline
            rows={2}
          />
        </Box>

        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Series Image
            </Typography>
            <ImageContainerPreview
              title="Series Image"
              image={state.series_image_preview || state.series_image}
              name="series_image"
              handleRemove={() => setState(prev => ({ ...prev, series_image: "", series_image_preview: null }))}
              handleImageUpload={handleImageUpload}
              showCopyUrlButton={true}
              sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
            />
          </Box>

          <Box gridColumn={{ md: 'span 2' }}>
            <Typography variant="h6" gutterBottom>
              Deep Link Settings
            </Typography>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 2fr' }} gap={3}>
              <ImageContainerPreview
                title="Deep Link Image"
                image={state.series_text_image_url_preview || state.series_text_image_url}
                name="deep_link_image"
                handleRemove={() => setState(prev => ({ ...prev, series_text_image_url: "", series_text_image_url_preview: null }))}
                handleImageUpload={handleDeepLinkImageUpload}
                showCopyUrlButton={true}
                sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
              />
              <UniversalLinkField url={state.universal_link} />
            </Box>
          </Box>
        </Box>

        {isEdit && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LibraryBooks /> Sessions
            </Typography>
            <Paper sx={{ p: 2 }}>
              <SortableSeriesList
                items={sortableItems}
                onSortEnd={({ oldIndex, newIndex }) => {
                  const newOrder = [...state.sortOrder];
                  const [removed] = newOrder.splice(oldIndex, 1);
                  newOrder.splice(newIndex, 0, removed);
                  setState(prev => ({ ...prev, sortOrder: newOrder }));
                }}
                onSessionDataChange={handleSessionDataChange}
              />
            </Paper>
          </Box>
        )}

        {isEdit && (
          <Box sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
            >
              Delete Series
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
});

AddEditSeries.displayName = 'AddEditSeries';

export default AddEditSeries;
